/* engine/fabrics.js — the Fabric Library as a configurator FINISH (batch 14, 2026-10-03).
 *
 * User: "add this logic for the fabric library: when we upload an image, the 3D model takes
 * the leather (for example) from the image we put". The technician's Fabric Library
 * (blob.fabrics) is zones → groups (price groups) → designs → colours; a colour is a hex
 * swatch and/or an uploaded PHOTO (`tex`, with the mm it covers — `texWmm`). A sofa,
 * armchair or outdoor lounger picks ONE colour; the photo becomes the material of the
 * model's upholstery meshes (GLB role "fabric": names like Fabric / Cushion / Seat /
 * Leather …) at its true real-world scale, in the 3D view, the module editor and Present.
 *
 * TOKEN: "fab:<zone>:<groupKey>:<designIdx>:<colourIdx>" — the group KEY is stable
 * (in-pg1, lt2, out-pg1 …); design / colour are positions inside it.
 * FAMILIES: Indoor products use Indoor Fabric + Leather, Outdoor products Outdoor Fabric +
 * Rope; kitchens / systems have no fabric. PER PRODUCT the technician's allow-list columns
 * gate the GROUPS (u.opts.fabric / leather / outfabric / rope — empty = everything).
 * PRICE: the group's `price` (€, flat per module) — OPT-FABRIC-<groupKey>.
 * Python twin: backend/fabrics.py (validation 422 + the quote delta) — JS ≡ PY. */

export const FABRIC_ZONES = ["indoor", "leather", "outdoor", "rope"];
export const FABRIC_ZONE_LABEL = { indoor: "Indoor Fabric", leather: "Leather", outdoor: "Outdoor Fabric", rope: "Rope" };
export const FABRIC_ZONES_BY_FAMILY = { indoor: ["indoor", "leather"], outdoor: ["outdoor", "rope"] };
/** the technician's allow-list column per zone (Products page "Components & allowed options") */
export const FABRIC_OPT_CAT = { indoor: "fabric", leather: "leather", outdoor: "outfabric", rope: "rope" };
/** surface response per zone: woven fabric is matte, leather has a soft sheen */
export const FABRIC_ROUGHNESS = { indoor: 0.92, leather: 0.5, outdoor: 0.88, rope: 0.84 };
/** the mm a fabric photo covers when the technician did not say (a typical swatch) */
export const FABRIC_TILE_MM = 300;

export function fabricToken(zone, groupKey, di, ci) { return "fab:" + zone + ":" + groupKey + ":" + di + ":" + ci; }

export function parseFabricToken(tok) {
  const m = /^fab:([a-z]+):(.+):(\d+):(\d+)$/.exec(String(tok || ""));
  if (!m || FABRIC_ZONES.indexOf(m[1]) < 0) return null;
  return { zone: m[1], groupKey: m[2], di: +m[3], ci: +m[4] };
}

/** the product line of a def or a technician unit (kitchens by default) */
export function familyOf(x) { return String((x && (x.family || x.group)) || "kitchens").toLowerCase(); }
export function fabricZonesFor(family) { return FABRIC_ZONES_BY_FAMILY[String(family || "").toLowerCase()] || []; }
export function hasFabrics(x) { return fabricZonesFor(familyOf(x)).length > 0; }

function allows(unit, cat, id) {
  const l = unit && unit.opts && unit.opts[cat];
  if (!Array.isArray(l) || !l.length) return true;            // "None means no restriction"
  return l.some((v) => String(v) === String(id));
}

/** resolveFabric(blob, tok) → { zone, groupKey, di, ci, group, design, color, label, price } | null */
export function resolveFabric(blob, tok) {
  const t = parseFabricToken(tok);
  if (!t) return null;
  const groups = ((blob && blob.fabrics) || {})[t.zone];
  const group = Array.isArray(groups) ? groups.find((g) => g && String(g.key) === t.groupKey) : null;
  if (!group) return null;
  const design = (group.designs || [])[t.di];
  const color = design && (design.colors || [])[t.ci];
  if (!color) return null;
  const label = (design.name || "Design") + " · " + (color.name || ("Colour " + (t.ci + 1)));
  const price = Number(group.price) > 0 ? Number(group.price) : 0;
  return { ...t, group, design, color, label, price };
}

/** may this product (technician unit, or a def carrying family) wear this fabric? */
export function isFabricAllowed(blob, unit, tok) {
  const f = resolveFabric(blob, tok);
  if (!f) return false;
  if (fabricZonesFor(familyOf(unit)).indexOf(f.zone) < 0) return false;
  return allows(unit, FABRIC_OPT_CAT[f.zone], f.groupKey);
}

/** the option tree the module editor / wizard show for one product:
 *  [{ zone, label, groups: [{ key, label, price, designs: [{ name, colors: [{ id, label, hex, tex }] }] }] }] */
export function fabricOptionsFor(blob, unit) {
  const out = [];
  fabricZonesFor(familyOf(unit)).forEach((zone) => {
    const groups = (((blob && blob.fabrics) || {})[zone] || []).filter((g) => g && g.key != null && allows(unit, FABRIC_OPT_CAT[zone], g.key));
    const gs = groups.map((g) => ({
      key: String(g.key), label: g.label || String(g.key), price: Number(g.price) > 0 ? Number(g.price) : 0,
      designs: (g.designs || []).map((d, di) => ({
        name: (d && d.name) || "Design " + (di + 1),
        colors: ((d && d.colors) || []).map((c, ci) => ({
          id: fabricToken(zone, g.key, di, ci),
          label: (c && c.name) || "Colour " + (ci + 1),
          hex: (c && c.sw) || "#9A9BA0",
          tex: (c && c.tex) || null,
        })),
      })).filter((d) => d.colors.length),
    })).filter((g) => g.designs.length);
    if (gs.length) out.push({ zone, label: FABRIC_ZONE_LABEL[zone], groups: gs });
  });
  return out;
}

/** every colour token of an option tree (flat) */
export function fabricIdsOf(tree) {
  const ids = [];
  (tree || []).forEach((z) => z.groups.forEach((g) => g.designs.forEach((d) => d.colors.forEach((c) => ids.push(c.id)))));
  return ids;
}

/** the photo's real-world tile: the mm it covers (default a 300 mm swatch), the height from
 *  the photo's own aspect, quarter-turn rotation — the configVisual texOptsOf contract */
export function fabricTexOpts(color, zone) {
  const c = color || {};
  const px = Array.isArray(c.texPx) ? c.texPx : null;
  const w = c.texWmm > 0 ? Number(c.texWmm) : FABRIC_TILE_MM;
  const h = c.texHmm > 0 ? Number(c.texHmm) : (px && px[0] > 0 ? Math.round(w * px[1] / px[0]) : w);
  const rot = ((Math.round((Number(c.texRot) || 0) / 90) * 90) % 360 + 360) % 360;
  return { tileWmm: w, tileHmm: h, rotDeg: rot, roughness: FABRIC_ROUGHNESS[zone] != null ? FABRIC_ROUGHNESS[zone] : 0.9 };
}

/** the visual a fabric pick contributes (spread into resolveConfigVisual's output) */
export function fabricVisual(blob, tok) {
  const f = resolveFabric(blob, tok);
  if (!f) return {};
  const out = { fabricHex: f.color.sw || "#9A9BA0", fabricZone: f.zone, fabricRough: FABRIC_ROUGHNESS[f.zone] || 0.9 };
  if (f.color.tex) { out.fabricTex = f.color.tex; out.fabricTexOpts = fabricTexOpts(f.color, f.zone); }
  return out;
}

/** the quote delta of a fabric pick (the group's flat €) — JS ≡ PY fabrics.fabric_delta */
export function fabricDelta(blob, tok) {
  const f = resolveFabric(blob, tok);
  if (!f || !(f.price > 0)) return null;
  return { key: "fabric", code: "OPT-FABRIC-" + f.groupKey, label: FABRIC_ZONE_LABEL[f.zone] + " · " + f.label + " (" + (f.group.label || f.groupKey) + ")", val: Math.round(f.price * 100) / 100 };
}
